import { useCallback, useMemo, useRef, useState } from 'react';
import cppCompiler from './services/cppCompiler';
import cCompiler from './services/cCompiler';
import pythonCompiler from './services/pythonCompiler';
import javascriptCompiler from './services/javascriptCompiler';
import javaCompiler from './services/javaCompiler';

type Language = 'cpp' | 'c' | 'python' | 'javascript' | 'java' | 'c++' | string;

enum SupportedLanguage {
    CPP = 'cpp',
    C = 'c',
    PYTHON = 'python',
    JAVASCRIPT = 'javascript',
    JAVA = 'java',
    CPLUSPLUS = 'c++',
}
interface ExecuteResult {
  success: boolean;
  output: string;
  error?: string;
  executionTime: number;
  compilationError?: string;
  runtimeError?: string;
  errorType?: 'compilation' | 'runtime' | 'execution';
}

export interface TestCase {
  input: string;
  expectedOutput: string;
  fullExecutableCode: string;
}

export interface TestCaseResult {
  input: string;
  expectedOutput: string;
  actualOutput: string;
  status: 'pass' | 'fail' | 'error';
  error?: string;
  executionTime: number;
  compilationError?: string;
  runtimeError?: string;
  errorType?: 'compilation' | 'runtime' | 'execution';
}


interface UseExecutorHookReturn {
  executeCode: ({
    userCode,
    language,
    testCases
  }: {
    userCode: string,
    language: Language,
    testCases: TestCase[]
  }) => Promise<TestCaseResult[]>;

  isLoading: boolean;
  error: string | null;
  result: TestCaseResult[] | null;
  testResults: TestCaseResult[];
}

export function useExecutorHook(): UseExecutorHookReturn {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<TestCaseResult[] | null>(null);
  const [testResults, setTestResults] = useState<TestCaseResult[]>([]);
  // Track the latest execution to avoid older runs overwriting newer results
  const latestExecIdRef = useRef(0);


  const ensureCReady = useCallback(async () => {
    if (!cCompiler.isReady()) {
      await cCompiler.initialize();
    }
  }, []);

  const ensurePythonReady = useCallback(async () => {
    // Python compiler initializes immediately, no need to wait
    if (!pythonCompiler.isReady()) {
      await pythonCompiler.initialize();
    }
  }, []);

  const ensureJavaScriptReady = useCallback(async () => {
    // JavaScript compiler initializes immediately, no need to wait
    if (!javascriptCompiler.isReady()) {
      await javascriptCompiler.initialize();
    }
  }, []);

  const ensureJavaReady = useCallback(async () => {
    if (!javaCompiler.isReady()) {
      await javaCompiler.initialize();
    }
  }, []);

  const runCpp = useCallback(async (code: string, testCases: TestCase[]): Promise<ExecuteResult[]> => {
    return cppCompiler.executeCodeWithTestCases(code, testCases);
  }, []);

  const runC = useCallback(async (code: string, testCases: TestCase[]): Promise<ExecuteResult[]> => {
    await ensureCReady();
    return cCompiler.executeCodeWithTestCases(code, testCases);
  }, [ensureCReady]);

  const runPython = useCallback(async (code: string, testCases: TestCase[]): Promise<ExecuteResult[]> => {
    await ensurePythonReady();
    return pythonCompiler.executeCodeWithTestCases(code, testCases);
  }, [ensurePythonReady]);

  const runJavaScript = useCallback(async (code: string, testCases: TestCase[]): Promise<ExecuteResult[]> => {
    await ensureJavaScriptReady();
    return javascriptCompiler.executeCodeWithTestCases(code, testCases);
  }, [ensureJavaScriptReady]);

  const runJava = useCallback(async (code: string, testCases: TestCase[]): Promise<ExecuteResult[]> => {
    await ensureJavaReady();
    return javaCompiler.executeCodeWithTestCases(code, testCases);
  }, [ensureJavaReady]);


  const compareResultsWithTestCases = (results: ExecuteResult[], testCases: TestCase[]) => {
    const testCaseResults: TestCaseResult[] = [];
    results.forEach((result, index) => {
        const testCase = testCases[index];
        const testCaseResult: TestCaseResult = {
          ...testCase,
          ...result,
          actualOutput: result.output,
          status: 'fail' 
        };
        
        // Handle different error types
        if (!result.success) {
          if (result.compilationError) {
            testCaseResult.status = 'error';
            testCaseResult.error = result.compilationError;
            testCaseResult.errorType = 'compilation';
            testCaseResult.actualOutput = `Compilation Error: ${result.compilationError}`;
          } else if (result.runtimeError) {
            testCaseResult.status = 'error';
            testCaseResult.error = result.runtimeError;
            testCaseResult.errorType = 'runtime';
            testCaseResult.actualOutput = `Runtime Error: ${result.runtimeError}`;
          } else if (result.error) {
            testCaseResult.status = 'error';
            testCaseResult.error = result.error;
            testCaseResult.errorType = 'execution';
            testCaseResult.actualOutput = `Execution Error: ${result.error}`;
          }
        } else {
          // Only compare outputs if execution was successful
          if(testCaseResult.actualOutput === testCaseResult.expectedOutput){
            testCaseResult.status = 'pass';
          } else {
            testCaseResult.status = 'fail';
          }
        }
        
        testCaseResults.push(testCaseResult);
    });
    return testCaseResults;
  }



  const executeCode = useCallback<UseExecutorHookReturn['executeCode']>(
    async ({userCode, language, testCases}) => {
      const execId = ++latestExecIdRef.current;
      setIsLoading(true);
      setError(null);
      setResult(null);
      setTestResults([]);

      try {
        
        const isSupportedLanguage = Object.values(SupportedLanguage).includes(language as SupportedLanguage);
        if(!isSupportedLanguage) throw new Error(`Unsupported language: ${language}. Only 'cpp', 'c', 'python', 'javascript', and 'java' are currently enabled.`);
        

        let exec: ExecuteResult[] = [];
        switch(language){
            case SupportedLanguage.CPP:
                exec = await runCpp(userCode, testCases);
                break;
            case SupportedLanguage.C:
                exec = await runC(userCode, testCases);
                break;
            case SupportedLanguage.PYTHON:
                exec = await runPython(userCode, testCases);
                break;
            case SupportedLanguage.JAVASCRIPT:
                exec = await runJavaScript(userCode, testCases);
                break;
            case SupportedLanguage.JAVA:
                exec = await runJava(userCode, testCases);
                break;
            case SupportedLanguage.CPLUSPLUS:
                exec = await runCpp(userCode, testCases);
                break;
            default:
                throw new Error(`Unsupported language: ${language}. Only 'cpp', 'c', 'python', 'javascript', and 'java' are currently enabled.`);
        }

     
        const testCaseResults = compareResultsWithTestCases(exec, testCases);
        // Only apply results if this is the latest execution
        if (latestExecIdRef.current === execId) {
          setTestResults(testCaseResults);
          setResult(testCaseResults);
        }
        return testCaseResults;
        
      } catch (err) {
        const message = err instanceof Error ? err.message : 'Execution failed';

        // Only set error if still latest
        if (latestExecIdRef.current === execId) {
          setError(message);
        }
        return [] as TestCaseResult[];
      } finally {
        if (latestExecIdRef.current === execId) {
          setIsLoading(false);
        }
      }
    },
    [runCpp, runC, runPython, runJavaScript, runJava]
  );


  return useMemo(
    () => ({ executeCode,  isLoading, error, result, testResults }),
    [executeCode,  isLoading, error, result, testResults]
  );
}

export default useExecutorHook;

